import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Flame, Drumstick, X, ChevronUp, ChevronDown, Trash2, Filter, AlertTriangle } from "lucide-react";
import { useCart, API } from "@/App";
import { useLanguage } from "@/LanguageContext";
import { toast } from "sonner";

// Common allergens with Norwegian abbreviations
const ALLERGENS = [
  { code: "G", name: "Gluten", nameEn: "Gluten" },
  { code: "M", name: "Melk", nameEn: "Milk" },
  { code: "E", name: "Egg", nameEn: "Egg" },
  { code: "N", name: "Nøtter", nameEn: "Nuts" },
  { code: "P", name: "Peanøtter", nameEn: "Peanuts" },
  { code: "S", name: "Skalldyr", nameEn: "Shellfish" },
  { code: "F", name: "Fisk", nameEn: "Fish" },
  { code: "SO", name: "Soya", nameEn: "Soy" },
  { code: "SE", name: "Selleri", nameEn: "Celery" },
  { code: "SN", name: "Sennep", nameEn: "Mustard" },
];

const MenuPage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [popularItems, setPopularItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [meatChoice, setMeatChoice] = useState("chicken");
  const [spiceLevel, setSpiceLevel] = useState(3);
  const [cartExpanded, setCartExpanded] = useState(false);
  const [allergenFilter, setAllergenFilter] = useState([]);
  const [showAllergenFilter, setShowAllergenFilter] = useState(false);
  const { cart, addToCart, removeFromCart, updateQuantity } = useCart();
  const { lang, t } = useLanguage();

  const cartTotal = cart.reduce((sum, item) => sum + item.total_price, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const categories = [
    { id: "all", key: "menu_all" },
    { id: "forretter", key: "cat_forretter" },
    { id: "tandoori", key: "cat_tandoori" },
    { id: "chef_special", key: "cat_chef_special" },
    { id: "traditional", key: "cat_traditional" },
    { id: "vegetar", key: "cat_vegetar" },
    { id: "seafood", key: "cat_seafood" },
    { id: "nan", key: "cat_nan" },
    { id: "tilbehor", key: "cat_tilbehor" },
    { id: "barn", key: "cat_barn" },
  ];

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const [menuRes, popularRes] = await Promise.all([
          axios.get(`${API}/menu`),
          axios.get(`${API}/menu/popular?limit=5`).catch(() => ({ data: [] })),
        ]);
        setMenuItems(menuRes.data.filter(item => item.available));
        setPopularItems(popularRes.data.map(p => p.name));
      } catch (error) {
        console.error("Error fetching menu:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  const filteredItems = menuItems.filter((item) => {
    // Category filter
    if (activeCategory !== "all" && item.category !== activeCategory) {
      return false;
    }
    
    // Allergen filter - exclude items containing selected allergens
    if (allergenFilter.length > 0 && item.allergens) {
      const itemAllergens = item.allergens.toUpperCase().split(/[,\s]+/).map(a => a.trim());
      for (const selectedAllergen of allergenFilter) {
        if (itemAllergens.includes(selectedAllergen.toUpperCase())) {
          return false;
        }
      }
    }
    
    return true;
  }).sort((a, b) => {
    const aPopular = popularItems.includes(a.name) ? 0 : 1;
    const bPopular = popularItems.includes(b.name) ? 0 : 1;
    return aPopular - bPopular;
  });

  const toggleAllergen = (code) => {
    setAllergenFilter(prev => 
      prev.includes(code) 
        ? prev.filter(a => a !== code)
        : [...prev, code]
    );
  };

  const clearAllergenFilter = () => {
    setAllergenFilter([]);
  };

  const handleAddClick = (item) => {
    if (item.has_meat_choice || item.has_spice_choice) {
      setSelectedItem(item);
      setMeatChoice("chicken");
      setSpiceLevel(3);
    } else {
      addToCart({
        ...item,
        cart_key: item.id,
        unit_price: item.price,
        total_price: item.price,
      });
      toast.success(`${item.name} ${t("menu_added_toast")}`);
    }
  };

  const handleConfirmAdd = () => {
    if (!selectedItem) return;

    let finalPrice = selectedItem.price;
    let itemName = selectedItem.name;

    if (selectedItem.has_meat_choice && meatChoice === "lamb") {
      finalPrice += selectedItem.lamb_extra_price || 0;
      itemName += lang === "en" ? " (Lamb)" : " (Lam)";
    } else if (selectedItem.has_meat_choice) {
      itemName += lang === "en" ? " (Chicken)" : " (Kylling)";
    }

    if (selectedItem.has_spice_choice) {
      const spiceNames = ["", "Mild", "Mild+", "Medium", "Medium+", lang === "en" ? "Hot" : "Sterk", lang === "en" ? "Extra Hot" : "X-Sterk"];
      itemName += ` - ${spiceNames[spiceLevel]}`;
    }

    const cartKey = `${selectedItem.id}_${selectedItem.has_meat_choice ? meatChoice : "none"}_${selectedItem.has_spice_choice ? spiceLevel : "none"}`;

    addToCart({
      ...selectedItem,
      cart_key: cartKey,
      name: itemName,
      unit_price: finalPrice,
      total_price: finalPrice,
      meat_choice: selectedItem.has_meat_choice ? meatChoice : null,
      spice_level: selectedItem.has_spice_choice ? spiceLevel : null,
    });

    toast.success(`${itemName} ${t("menu_added_toast")}`);
    setSelectedItem(null);
  };

  const getDescription = (item) => {
    if (lang === "en" && item.description_en) return item.description_en;
    return item.description;
  };

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      {/* Header */}
      <div className="py-16 mandala-pattern" style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="indian-divider mb-4">
            <span className="text-sm uppercase tracking-widest" style={{ color: '#d4af37' }}>{t("menu_tag")}</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl sm:text-5xl font-serif-display font-semibold italic mb-4" style={{ color: '#d4af37' }} data-testid="menu-page-title">
            {t("menu_title")}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} style={{ color: '#f5e6d3' }}>
            {t("menu_subtitle")}
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="text-xs mt-2" style={{ color: '#d4af37' }}>
            {t("menu_takeaway")}
          </motion.p>
        </div>
      </div>

      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-4 justify-center" data-testid="category-filter">
          {categories.map((cat) => (
            <Button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`rounded-full text-sm transition-all ${activeCategory === cat.id ? 'btn-maroon shadow-lg' : 'bg-white hover:bg-stone-50'}`}
              style={activeCategory !== cat.id ? { border: '1px solid #b27f47', color: '#2d1810' } : {}}
              data-testid={`category-${cat.id}`}
            >
              {t(cat.key)}
            </Button>
          ))}
        </div>

        {/* Allergen Filter Toggle */}
        <div className="flex justify-center mb-6">
          <Button
            onClick={() => setShowAllergenFilter(!showAllergenFilter)}
            variant="outline"
            className={`rounded-full text-sm flex items-center gap-2 ${allergenFilter.length > 0 ? 'bg-orange-50' : ''}`}
            style={{ 
              border: allergenFilter.length > 0 ? '2px solid #f97316' : '1px solid #b27f47', 
              color: allergenFilter.length > 0 ? '#ea580c' : '#2d1810' 
            }}
            data-testid="allergen-filter-toggle"
          >
            <Filter className="h-4 w-4" />
            {lang === "en" ? "Allergen Filter" : "Allergenfiltrer"}
            {allergenFilter.length > 0 && (
              <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: '#f97316', color: 'white' }}>
                {allergenFilter.length}
              </span>
            )}
          </Button>
        </div>

        {/* Allergen Filter Panel */}
        <AnimatePresence>
          {showAllergenFilter && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 overflow-hidden"
            >
              <div 
                className="bg-white rounded-xl p-4 shadow-md"
                style={{ border: '2px solid #d4af37' }}
                data-testid="allergen-filter-panel"
              >
                <div className="flex items-start gap-2 mb-3">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#f97316' }} />
                  <div>
                    <p className="font-semibold text-sm" style={{ color: '#2d1810' }}>
                      {lang === "en" ? "Exclude dishes containing:" : "Ekskluder retter som inneholder:"}
                    </p>
                    <p className="text-xs" style={{ color: '#8b4513' }}>
                      {lang === "en" 
                        ? "Select allergens to hide dishes that contain them" 
                        : "Velg allergener for å skjule retter som inneholder dem"}
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {ALLERGENS.map((allergen) => (
                    <button
                      key={allergen.code}
                      onClick={() => toggleAllergen(allergen.code)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        allergenFilter.includes(allergen.code)
                          ? 'bg-orange-500 text-white shadow-md'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                      style={!allergenFilter.includes(allergen.code) ? { color: '#4a3728' } : {}}
                      data-testid={`allergen-${allergen.code}`}
                    >
                      <span className="font-bold">{allergen.code}</span>
                      <span className="ml-1">- {lang === "en" ? allergen.nameEn : allergen.name}</span>
                    </button>
                  ))}
                </div>

                {allergenFilter.length > 0 && (
                  <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
                    <p className="text-xs" style={{ color: '#8b4513' }}>
                      {lang === "en" 
                        ? `Showing ${filteredItems.length} dishes without selected allergens`
                        : `Viser ${filteredItems.length} retter uten valgte allergener`}
                    </p>
                    <button
                      onClick={clearAllergenFilter}
                      className="text-xs font-medium px-3 py-1 rounded-full hover:bg-gray-100"
                      style={{ color: '#721111' }}
                      data-testid="clear-allergen-filter"
                    >
                      {lang === "en" ? "Clear all" : "Fjern alle"}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {loading ? (
          <p className="text-center py-12" style={{ color: '#4a3728' }}>{t("loading")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all"
                style={{ border: '2px solid #d4af37' }}
                data-testid={`menu-item-${item.id}`}
              >
                {item.image_url && (
                  <div className="h-48 overflow-hidden relative">
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                    {popularItems.includes(item.name) && (
                      <span
                        className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold"
                        style={{ background: '#d4af37', color: '#2d1810' }}
                        data-testid="popular-badge"
                      >
                        Populær
                      </span>
                    )}
                  </div>
                )}
                {!item.image_url && popularItems.includes(item.name) && (
                  <span
                    className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold"
                    style={{ background: '#d4af37', color: '#2d1810' }}
                    data-testid="popular-badge"
                  >
                    Populær
                  </span>
                )}
                <div className="p-5">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-serif-display font-semibold" style={{ color: '#2d1810' }}>{item.name}</h3>
                    <span className="text-lg font-bold whitespace-nowrap ml-2" style={{ color: '#721111' }}>kr {item.price},-</span>
                  </div>
                  <p className="text-sm mb-3 line-clamp-2" style={{ color: '#4a3728' }}>{getDescription(item)}</p>
                  {item.allergens && (
                    <p className="text-xs mb-3" style={{ color: '#8b4513' }}>{t("menu_allergens")}: {item.allergens}</p>
                  )}
                  <Button
                    onClick={() => handleAddClick(item)}
                    className="w-full rounded-full btn-maroon transition-transform hover:scale-105 active:scale-95"
                    data-testid={`add-to-cart-${item.id}`}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    {item.has_meat_choice || item.has_spice_choice ? t("menu_choose_add") : t("menu_add")}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Variant Selection Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelectedItem(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
            style={{ border: '3px solid #d4af37' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="variant-modal"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-serif-display font-bold" style={{ color: '#2d1810' }}>{t("menu_choose_options")}</h3>
              <button onClick={() => setSelectedItem(null)} style={{ color: '#721111' }}><X className="h-5 w-5" /></button>
            </div>

            <p className="text-lg font-semibold mb-4" style={{ color: '#721111' }}>{selectedItem.name}</p>

            {selectedItem.has_meat_choice && (
              <div className="mb-5">
                <label className="block text-sm font-semibold mb-2" style={{ color: '#2d1810' }}>{t("menu_meat_choice")}</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setMeatChoice("chicken")}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${meatChoice === "chicken" ? 'bg-amber-50' : ''}`}
                    style={{ borderColor: meatChoice === "chicken" ? '#d4af37' : '#e5e7eb' }}
                    data-testid="meat-chicken"
                  >
                    <Drumstick className="h-5 w-5 mx-auto mb-1" style={{ color: meatChoice === "chicken" ? '#721111' : '#ccc' }} />
                    <div className="font-semibold text-sm" style={{ color: '#2d1810' }}>{t("menu_chicken")}</div>
                    <div className="text-sm" style={{ color: '#721111' }}>kr {selectedItem.price},-</div>
                  </button>
                  <button
                    onClick={() => setMeatChoice("lamb")}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${meatChoice === "lamb" ? 'bg-amber-50' : ''}`}
                    style={{ borderColor: meatChoice === "lamb" ? '#d4af37' : '#e5e7eb' }}
                    data-testid="meat-lamb"
                  >
                    <Drumstick className="h-5 w-5 mx-auto mb-1" style={{ color: meatChoice === "lamb" ? '#721111' : '#ccc' }} />
                    <div className="font-semibold text-sm" style={{ color: '#2d1810' }}>{t("menu_lamb")}</div>
                    <div className="text-sm" style={{ color: '#721111' }}>kr {selectedItem.price + (selectedItem.lamb_extra_price || 0)},- <span className="text-xs" style={{ color: '#8b4513' }}>+{selectedItem.lamb_extra_price || 0}kr</span></div>
                  </button>
                </div>
              </div>
            )}

            {selectedItem.has_spice_choice && (
              <div className="mb-5">
                <label className="block text-sm font-semibold mb-2" style={{ color: '#2d1810' }}>{t("menu_spice_level")}</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { level: 1, label: "Mild", flames: 1 },
                    { level: 2, label: "Mild+", flames: 2 },
                    { level: 3, label: "Medium", flames: 3 },
                    { level: 4, label: "Medium+", flames: 3 },
                    { level: 5, label: lang === "en" ? "Hot" : "Sterk", flames: 4 },
                    { level: 6, label: lang === "en" ? "Extra Hot" : "X-Sterk", flames: 5 },
                  ].map(({ level, label, flames }) => (
                    <button
                      key={level}
                      onClick={() => setSpiceLevel(level)}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${spiceLevel === level ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}
                    >
                      <div className="flex justify-center mb-1">
                        {[...Array(flames)].map((_, i) => (
                          <Flame key={i} className="h-3 w-3" style={{ color: spiceLevel === level ? '#ff6b00' : '#ccc' }} />
                        ))}
                      </div>
                      <div className="text-xs" style={{ color: '#4a3728' }}>{label}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4" style={{ borderTop: '2px solid #d4af37' }}>
              <div className="flex justify-between items-center mb-4">
                <span className="font-semibold" style={{ color: '#2d1810' }}>{t("menu_total")}:</span>
                <span className="text-2xl font-bold" style={{ color: '#721111' }} data-testid="variant-total-price">
                  kr {selectedItem.price + (selectedItem.has_meat_choice && meatChoice === "lamb" ? (selectedItem.lamb_extra_price || 0) : 0)},-
                </span>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setSelectedItem(null)} variant="outline" className="flex-1 rounded-full" style={{ borderColor: '#b27f47', color: '#2d1810' }}>
                  {t("menu_cancel")}
                </Button>
                <Button onClick={handleConfirmAdd} className="flex-1 rounded-full btn-maroon" data-testid="confirm-add-to-cart">
                  <ShoppingCart className="h-4 w-4 mr-2" /> {t("menu_add_to_cart")}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Floating Cart Summary */}
      <AnimatePresence>
        {cartCount > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 z-40 shadow-2xl"
            style={{ background: '#2d1810', borderTop: '3px solid #d4af37' }}
          >
            {/* Expandable Cart Details */}
            <AnimatePresence>
              {cartExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="max-w-4xl mx-auto px-4 py-4 max-h-64 overflow-y-auto">
                    {cart.map((item) => (
                      <div key={item.cart_key} className="flex items-center justify-between py-2 border-b" style={{ borderColor: 'rgba(212, 175, 55, 0.3)' }}>
                        <div className="flex-1">
                          <p className="font-medium text-sm" style={{ color: '#f5e6d3' }}>{item.name}</p>
                          <p className="text-xs" style={{ color: '#d4af37' }}>kr {item.total_price},-</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.cart_key, item.quantity - 1)}
                            className="w-7 h-7 rounded-full flex items-center justify-center"
                            style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#d4af37' }}
                          >
                            -
                          </button>
                          <span className="w-6 text-center text-sm" style={{ color: '#f5e6d3' }}>{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.cart_key, item.quantity + 1)}
                            className="w-7 h-7 rounded-full flex items-center justify-center"
                            style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#d4af37' }}
                          >
                            +
                          </button>
                          <button
                            onClick={() => removeFromCart(item.cart_key)}
                            className="ml-2 p-1 rounded"
                            style={{ color: '#ef4444' }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Cart Bar */}
            <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
              <button
                onClick={() => setCartExpanded(!cartExpanded)}
                className="flex items-center gap-3"
              >
                <div className="relative">
                  <ShoppingCart className="h-6 w-6" style={{ color: '#d4af37' }} />
                  <span 
                    className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: '#d4af37', color: '#2d1810' }}
                  >
                    {cartCount}
                  </span>
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium" style={{ color: '#f5e6d3' }}>
                    {cartCount} {cartCount === 1 ? 'vare' : 'varer'}
                  </p>
                  <p className="text-lg font-bold" style={{ color: '#d4af37' }}>kr {cartTotal},-</p>
                </div>
                {cartExpanded ? (
                  <ChevronDown className="h-5 w-5 ml-2" style={{ color: '#d4af37' }} />
                ) : (
                  <ChevronUp className="h-5 w-5 ml-2" style={{ color: '#d4af37' }} />
                )}
              </button>
              
              <Link to="/order">
                <Button className="btn-gold px-8 py-3 text-base font-semibold rounded-full">
                  {t("menu_checkout") || "Gå til Kasse"}
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default MenuPage;
